import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Play, Download, Loader2, Network, Target, ArrowRight, Network as NetworkIcon } from 'lucide-react';
import { CustomCodeAssistant } from './CustomCodeAssistant';
import { ImpactAnalyzer } from './ImpactAnalyzer';
import { FioriPathfinder } from './FioriPathfinder';
import { ReleaseNotesMiner } from './ReleaseNotesMiner';
import { ATCAnalysis } from './ATCAnalysis';
import { OrgStructure } from './discovery/OrgStructure';
import { MasterDataAnalysis } from './discovery/MasterDataAnalysis';
import { TCodeAnalysis } from './discovery/TCodeAnalysis';
import { SecurityAnalysis } from './discovery/SecurityAnalysis';
import { RICEFWAnalysis } from './discovery/RICEFWAnalysis';
import { BusinessProcesses } from './discovery/BusinessProcesses';
import { IntegrationAnalysis } from './discovery/IntegrationAnalysis';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const DiscoveryEngine = ({ project, onNavigateToLandscape }) => {
  const [activeSubModule, setActiveSubModule] = useState('overview');
  const [landscapes, setLandscapes] = useState([]);
  const [selectedLandscape, setSelectedLandscape] = useState(null);
  const [loadingLandscapes, setLoadingLandscapes] = useState(true);
  const [runningDiscovery, setRunningDiscovery] = useState(false);
  const [discoveryComplete, setDiscoveryComplete] = useState(false);

  useEffect(() => {
    fetchLandscapes();
  }, [project.id]);

  const fetchLandscapes = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/landscapes`);
      setLandscapes(response.data);
      if (response.data.length > 0) {
        setSelectedLandscape(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching landscapes:', error);
    } finally {
      setLoadingLandscapes(false);
    }
  };

  const runCompleteDiscovery = async () => {
    if (!selectedLandscape) {
      alert('⚠️ Please configure a landscape first.\\n\\nGo to SAP Landscape module to set up your systems.');
      return;
    }
    
    setRunningDiscovery(true);
    try {
      await axios.post(`${API}/projects/${project.id}/discovery/run-complete`);
      setDiscoveryComplete(true);
      alert('✅ Complete Discovery Analysis Finished!\\n\\nView individual modules for detailed insights.');
    } catch (error) {
      console.error('Error running discovery:', error);
      alert('❌ Discovery failed. Please try again.');
    } finally {
      setRunningDiscovery(false);
    }
  };

  const handleGoToLandscape = () => {
    if (onNavigateToLandscape) {
      onNavigateToLandscape();
    }
  };

  const navigateToModule = (moduleId) => {
    setActiveSubModule(moduleId);
  };

  const subModules = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'org', label: 'Org Structure', icon: '🏢' },
    { id: 'masterdata', label: 'Master Data', icon: '💾' },
    { id: 'tcodes', label: 'T-Codes', icon: '⌨️' },
    { id: 'security', label: 'Security', icon: '🔐' },
    { id: 'ricefw', label: 'RICEFW', icon: '🔧' },
    { id: 'processes', label: 'Business Processes', icon: '📋' },
    { id: 'integrations', label: 'Integrations', icon: '🔌' },
    { id: 'atc', label: 'ATC Analysis', icon: '🧪' },
    { id: 'code', label: 'Code Assistant', icon: '💻' },
    { id: 'impact', label: 'Impact Analyzer', icon: '🎯' },
    { id: 'fiori', label: 'Fiori Mapping', icon: '🔄' },
    { id: 'releases', label: 'Release Notes', icon: '📝' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Search className="w-12 h-12" />
            <div>
              <h2 className="text-3xl font-bold">Discovery Engine</h2>
              <p className="text-blue-100 mt-1">Comprehensive SAP system analysis and discovery</p>
            </div>
          </div>
          <button
            onClick={runCompleteDiscovery}
            disabled={runningDiscovery || !selectedLandscape}
            className="px-6 py-3 bg-white text-purple-700 rounded-xl hover:bg-purple-50 transition-colors font-medium disabled:opacity-50 flex items-center space-x-2"
          >
            {runningDiscovery ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Running...</span>
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                <span>Run Complete Discovery</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Landscape Selector */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 flex-1">
            <Network className="w-6 h-6 text-blue-600" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-700 mb-2">Analyzing Landscape:</p>
              {loadingLandscapes ? (
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              ) : landscapes.length === 0 ? (
                <div className="flex items-center space-x-3">
                  <span className="text-gray-500">No landscapes configured</span>
                  <button 
                    onClick={handleGoToLandscape}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Create Landscape
                  </button>
                </div>
              ) : (
                <select
                  value={selectedLandscape?.id || ''}
                  onChange={(e) => setSelectedLandscape(landscapes.find(l => l.id === e.target.value))}
                  className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium text-gray-900"
                >
                  {landscapes.map((landscape) => (
                    <option key={landscape.id} value={landscape.id}>
                      {landscape.name} ({landscape.systems_detail?.length || 0} systems)
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          
          {selectedLandscape && (
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-xs text-gray-600">Systems</p>
                <p className="text-2xl font-bold text-blue-600">{selectedLandscape.systems_detail?.length || 0}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-600">Type</p>
                <p className="text-sm font-bold text-gray-900 capitalize">{selectedLandscape.landscape_type}</p>
              </div>
            </div>
          )}
        </div>
        
        {selectedLandscape && selectedLandscape.systems_detail && selectedLandscape.systems_detail.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-3">Discovered Systems:</p>
            <div className="flex flex-wrap gap-2">
              {selectedLandscape.systems_detail.map((system) => (
                <span key={system.id} className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium">
                  {system.system_id} ({system.system_type})
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sub-Module Navigation */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-wrap gap-2">
          {subModules.map((module) => (
            <button
              key={module.id}
              onClick={() => setActiveSubModule(module.id)}
              className={`px-4 py-2 rounded-xl font-medium transition-all flex items-center space-x-2 ${
                activeSubModule === module.id
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span>{module.icon}</span>
              <span>{module.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div>
        {activeSubModule === 'overview' && (
          <DiscoveryOverview 
            project={project} 
            landscape={selectedLandscape}
            onNavigate={navigateToModule}
            onNavigateToLandscape={handleGoToLandscape}
            discoveryComplete={discoveryComplete}
          />
        )}
        {activeSubModule === 'org' && <OrgStructure project={project} landscape={selectedLandscape} />}
        {activeSubModule === 'masterdata' && <MasterDataAnalysis project={project} landscape={selectedLandscape} />}
        {activeSubModule === 'tcodes' && <TCodeAnalysis project={project} landscape={selectedLandscape} />}
        {activeSubModule === 'security' && <SecurityAnalysis project={project} landscape={selectedLandscape} />}
        {activeSubModule === 'ricefw' && <RICEFWAnalysis project={project} landscape={selectedLandscape} />}
        {activeSubModule === 'processes' && <BusinessProcesses project={project} landscape={selectedLandscape} />}
        {activeSubModule === 'integrations' && <IntegrationAnalysis project={project} landscape={selectedLandscape} />}
        {activeSubModule === 'atc' && <ATCAnalysis project={project} />}
        {activeSubModule === 'code' && <CustomCodeAssistant project={project} />}
        {activeSubModule === 'impact' && <ImpactAnalyzer project={project} />}
        {activeSubModule === 'fiori' && <FioriPathfinder project={project} />}
        {activeSubModule === 'releases' && <ReleaseNotesMiner project={project} />}
      </div>
    </div>
  );
};

const DiscoveryOverview = ({ project, landscape, onNavigate, discoveryComplete }) => {
  const discoveryModules = [
    { id: 'org', title: 'Org Structure', desc: 'Company codes, plants, sales orgs across all modules', count: '6 Modules', icon: '🏢', color: 'blue' },
    { id: 'masterdata', title: 'Master Data', desc: 'Materials, customers, vendors, GL accounts with issue detection', count: '45K+ Records', icon: '💾', color: 'purple' },
    { id: 'tcodes', title: 'Transaction Codes', desc: 'Standard and custom transaction usage analysis', count: '500+ T-Codes', icon: '⌨️', color: 'green' },
    { id: 'security', title: 'Security Analysis', desc: 'Roles, users, authorizations with security issues', count: '450 Roles', icon: '🔐', color: 'red' },
    { id: 'ricefw', title: 'RICEFW Objects', desc: 'Reports, Interfaces, Conversions, Enhancements, Forms, Workflows', count: '12 Objects', icon: '🔧', color: 'orange' },
    { id: 'processes', title: 'Business Processes', desc: 'End-to-end process flows with RICEFW mapping', count: '2 Processes', icon: '📋', color: 'indigo' },
    { id: 'integrations', title: 'External Integrations', desc: 'REST, SOAP, File, RFC interfaces', count: '4 Systems', icon: '🔌', color: 'cyan' },
    { id: 'atc', title: 'ATC Analysis', desc: 'ABAP Test Cockpit findings and AI analysis', count: 'Upload Reports', icon: '🧪', color: 'pink' },
    { id: 'code', title: 'Code Assistant', desc: 'Custom ABAP code compatibility analysis', count: '50 Objects', icon: '💻', color: 'teal' },
  ];

  const handleNavigateToLandscape = () => {
    if (onNavigate) {
      onNavigate('landscape');
    }
  };

  return (
    <div className="space-y-6">
      {/* Discovery Status */}
      {landscape && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
              <Network className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Landscape: {landscape.name}</h3>
              <p className="text-gray-700 mb-3">{landscape.description}</p>
              <div className="flex items-center space-x-4">
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-sm font-semibold">
                  {landscape.landscape_type} Configuration
                </span>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold">
                  {landscape.systems_detail?.length || 0} Systems Configured
                </span>
                {discoveryComplete && (
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-lg text-sm font-semibold flex items-center space-x-1">
                    <Target className="w-4 h-4" />
                    <span>Discovery Complete</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!landscape && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6 text-center">
          <NetworkIcon className="w-16 h-16 mx-auto mb-4 text-yellow-600" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Landscape Configured</h3>
          <p className="text-gray-700 mb-4">Configure a SAP landscape to enable automated discovery</p>
          <button 
            onClick={handleNavigateToLandscape}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
          >
            Go to SAP Landscape
          </button>
        </div>
      )}

      {/* Discovery Modules Grid */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Discovery Capabilities</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {discoveryModules.map((module) => (
            <div
              key={module.id}
              onClick={() => onNavigate(module.id)}
              className={`bg-white border-2 border-gray-200 rounded-2xl p-6 cursor-pointer hover:border-${module.color}-400 hover:shadow-xl transition-all duration-300 group transform hover:-translate-y-1`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-14 h-14 bg-gradient-to-br from-${module.color}-500 to-${module.color}-700 rounded-xl flex items-center justify-center text-3xl shadow-md group-hover:scale-110 transition-transform`}>
                  {module.icon}
                </div>
                <ArrowRight className={`w-6 h-6 text-gray-400 group-hover:text-${module.color}-600 group-hover:translate-x-1 transition-all`} />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-${module.color}-700 transition-colors">
                {module.title}
              </h4>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">{module.desc}</p>
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1.5 bg-${module.color}-100 text-${module.color}-700 rounded-lg text-xs font-semibold`}>
                  {module.count}
                </span>
                <span className="text-xs text-gray-500 group-hover:text-gray-700">Click to explore →</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
